import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { graphql } from 'react-relay';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import Form from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';

import {
	RefetchAudioButton,
	setCustomization,
	getCustomization,
	FlagsProviderContext,
	useFlags,
} from '@atlassian/ai-audio';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { useMutationInterop } from '@confluence/apollo-relay-interop';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';

import type {
	CustomizePanelUpdateAudioPreferenceMutation$data,
	CustomizePanelUpdateAudioPreferenceMutation$variables,
	ConfluenceLength,
	ConfluenceTone,
} from './__generated__/CustomizePanelUpdateAudioPreferenceMutation.graphql.ts';
import { DurationSelector } from './DurationSelector.tsx';
import { ToneSelector } from './ToneSelector.tsx';

const i18n = defineMessages({
	refetchAudioButtonText: {
		id: 'audio.audio-popup.customize-tab.refetch-audio-button',
		defaultMessage: 'Re-start briefing',
		description: 'Text for a button that refetches audio and plays it when clicked',
	},
	errorFlagTitle: {
		id: 'audio.audio-popup.customize-tab.error-flag-title',
		defaultMessage: 'Error updating audio preference',
		description: 'Title for the error flag when we fail to update the audio preference',
	},
	errorFlagDescription: {
		id: 'audio.audio-popup.customize-tab.error-flag-description',
		defaultMessage: 'Please try again later.',
		description: 'Description for the error flag when we fail to update the audio preference',
	},
});

const styles = cssMap({
	formWrapper: {
		paddingInline: token('space.100'),
		textAlign: 'left',
	},
	buttonWrapper: {
		marginTop: token('space.150'),
	},
});

interface CustomizationFormValues {
	length: ConfluenceLength | null;
	tone: ConfluenceTone | null;
	playbackSpeed: number | null;
}

const CustomizePanelInner = () => {
	const customization = getCustomization();
	const { cloudId } = useSessionData();
	const flags = useFlags();
	const { formatMessage } = useIntl();

	const defaultValues: CustomizationFormValues = {
		length: customization.length as ConfluenceLength,
		tone: customization.tone as ConfluenceTone,
		playbackSpeed: customization.playbackSpeed || 1.0,
	};

	const [commitMutation] = useMutationInterop<
		CustomizePanelUpdateAudioPreferenceMutation$data,
		CustomizePanelUpdateAudioPreferenceMutation$variables
	>(graphql`
		mutation CustomizePanelUpdateAudioPreferenceMutation(
			$input: ConfluenceUpdateAudioPreferenceInput!
			$cloudId: ID!
		) {
			confluence_updateAudioPreference(cloudId: $cloudId, input: $input) {
				success
				preference {
					playbackSpeed
					length
					tone
				}
			}
		}
	`);

	const handlePreferenceUpdate = useCallback(
		async (
			updatedPreferences: Partial<{
				length: ConfluenceLength;
				tone: ConfluenceTone;
				playbackSpeed: number;
			}>,
		) => {
			const currentCustomization = getCustomization();
			const newPreferences = { ...currentCustomization, ...updatedPreferences };

			// Optimistic update for immediate UI feedback
			setCustomization(newPreferences);

			const optimisticState = {
				...currentCustomization,
				...updatedPreferences,
			};

			const input = {
				length: newPreferences.length as ConfluenceLength,
				tone: newPreferences.tone as ConfluenceTone,
				playbackSpeed: newPreferences.playbackSpeed ?? 1.0,
			};

			setCustomization(optimisticState);

			try {
				const result = await commitMutation({
					variables: {
						input,
						cloudId,
					},
				});

				if (
					result.data?.confluence_updateAudioPreference?.success &&
					result.data.confluence_updateAudioPreference.preference
				) {
					const serverPrefs = result.data.confluence_updateAudioPreference.preference;

					setCustomization({
						...currentCustomization,
						...serverPrefs,
					});
				} else if (result.data?.confluence_updateAudioPreference?.success === false) {
					// Rollback to previous state on server error
					setCustomization(currentCustomization);

					flags?.showFlag({
						title: formatMessage(i18n.errorFlagTitle),
						description: formatMessage(i18n.errorFlagDescription),
						icon: <StatusErrorIcon color={token('color.icon.danger')} label="" />,
						isAutoDismiss: true,
					});
				}
			} catch {
				// Rollback to previous state on network/mutation error
				setCustomization(currentCustomization);
				flags?.showFlag({
					title: formatMessage(i18n.errorFlagTitle),
					description: formatMessage(i18n.errorFlagDescription),
					icon: <StatusErrorIcon color={token('color.icon.danger')} label="" />,
					isAutoDismiss: true,
				});
			}
		},
		[commitMutation, cloudId, flags, formatMessage],
	);

	return (
		<Box xcss={styles.formWrapper}>
			{fg('platform-design_system_team-form_conversion') ? (
				<Form<CustomizationFormValues> onSubmit={() => {}}>
					<Stack space="space.100">
						<ToneSelector
							onPreferenceUpdate={handlePreferenceUpdate}
							defaultValue={defaultValues.tone}
						/>
						<DurationSelector
							onPreferenceUpdate={handlePreferenceUpdate}
							defaultValue={defaultValues.length}
						/>
						<Box xcss={styles.buttonWrapper}>
							<RefetchAudioButton
								buttonText={i18n.refetchAudioButtonText}
								source="customizePanel"
							/>
						</Box>
					</Stack>
				</Form>
			) : (
				<Form<CustomizationFormValues> onSubmit={() => {}}>
					{({ formProps }) => (
						<form {...formProps}>
							<Stack space="space.100">
								<ToneSelector
									onPreferenceUpdate={handlePreferenceUpdate}
									defaultValue={defaultValues.tone}
								/>
								<DurationSelector
									onPreferenceUpdate={handlePreferenceUpdate}
									defaultValue={defaultValues.length}
								/>
								<Box xcss={styles.buttonWrapper}>
									<RefetchAudioButton
										buttonText={i18n.refetchAudioButtonText}
										source="customizePanel"
									/>
								</Box>
							</Stack>
						</form>
					)}
				</Form>
			)}
		</Box>
	);
};

type CustomizePanelProps = {} & WithFlagsProps;

export const CustomizePanel = withFlags(({ flags }: CustomizePanelProps) => {
	const flagsContextValue = useMemo(() => {
		return { flags };
	}, [flags]);

	return (
		<FlagsProviderContext.Provider value={flagsContextValue}>
			<CustomizePanelInner />
		</FlagsProviderContext.Provider>
	);
});
